import { tourRecorder } from "@/utils/tour-recording/tour-recorder";

export class AudioStreamer {
  private sampleRate: number = 24000;
  public gainNode: GainNode;

  // Analyser for lipsync
  public analyser: AnalyserNode;

  // AudioWorklet playback (runs on audio thread)
  private workletNode: AudioWorkletNode | null = null;
  private workletReady: boolean = false;
  private pendingChunks: Float32Array[] = [];

  public isPlaying: boolean = false;
  private isStreamComplete: boolean = false;

  public onComplete = () => {};

  constructor(public context: AudioContext) {
    this.gainNode = this.context.createGain();

    // Setup Analyser
    this.analyser = this.context.createAnalyser();
    this.analyser.fftSize = 256;
    this.gainNode.connect(this.analyser);
    this.analyser.connect(this.context.destination);
    try {
      tourRecorder.registerAudioSource(this.analyser);
    } catch {
      // Ignore
    }

    this.addPCM16 = this.addPCM16.bind(this);
    this._initWorklet();
  }

  private async _initWorklet() {
    try {
      await this.context.audioWorklet.addModule('/audio-playback-processor.js');

      this.workletNode = new AudioWorkletNode(
        this.context,
        'audio-playback-processor',
        { 
          outputChannelCount: [1],
          processorOptions: {
            sampleRate: this.context.sampleRate // Truyền sampleRate thực tế (thường là 48000)
          }
        },
      );
      this.workletNode.connect(this.gainNode);

      this.workletNode.port.onmessage = (e: MessageEvent) => {
        if (e.data.type === 'complete') {
          this.isPlaying = false;
          this.onComplete();
        }
      };

      this.workletReady = true;

      for (const chunk of this.pendingChunks) {
        this.workletNode.port.postMessage(
          { type: 'audio', samples: chunk },
          [chunk.buffer],
        );
      }
      this.pendingChunks = [];
    } catch (err) {
      console.error('[AudioStreamer] AudioWorklet init failed:', err);
      this.workletReady = false;
    }
  }

  private _processPCM16Chunk(chunk: Uint8Array): Float32Array {
    const float32 = new Float32Array(chunk.length / 2);
    const view = new DataView(chunk.buffer, chunk.byteOffset, chunk.byteLength);

    for (let i = 0; i < float32.length; i++) {
      float32[i] = view.getInt16(i * 2, true) / 32768;
    }
    return float32;
  }

  addPCM16(chunk: Uint8Array) {
    this.isStreamComplete = false;
    if (this.context.state === 'suspended') {
      this.context.resume().catch(() => {});
    }
    const float32 = this._processPCM16Chunk(chunk);

    if (this.workletReady && this.workletNode) {
      this.workletNode.port.postMessage(
        { type: 'audio', samples: float32 },
        [float32.buffer],
      );
      this.isPlaying = true;
    } else {
      this.pendingChunks.push(float32);
    }
  }

  stop() {
    this.isPlaying = false;
    this.isStreamComplete = true;
    this.pendingChunks = [];

    if (this.workletNode) {
      this.workletNode.port.postMessage({ type: 'stop' });
    }

    this.gainNode.gain.linearRampToValueAtTime(0, this.context.currentTime + 0.1);

    setTimeout(() => {
      this.gainNode.disconnect();
      this.gainNode = this.context.createGain();
      this.gainNode.connect(this.analyser);
      if (this.workletNode) {
        this.workletNode.disconnect();
        this.workletNode.connect(this.gainNode);
      }
    }, 200);
  }

  async resume() {
    if (this.context.state === 'suspended') {
      await this.context.resume();
    }
    this.isStreamComplete = false;
    this.gainNode.gain.setValueAtTime(1, this.context.currentTime);
    try {
      tourRecorder.registerAudioSource(this.analyser);
    } catch {}

    if (this.workletNode) {
      this.workletNode.port.postMessage({ type: 'resume' });
    }
  }

  complete() {
    this.isStreamComplete = true;
    if (this.workletNode) {
      this.workletNode.port.postMessage({ type: 'complete' });
    } else {
      this.onComplete();
    }
  }
}
